/**
 * Errores del árbol de preguntas (producto y médicas) que el monolito devuelve
 * cuando no puede servir el cuestionario:
 *
 * - 503 `{"error":"question_tree_unavailable"}`: el servicio de cuestionarios está
 *   caído. Es temporal: tiene sentido reintentar.
 * - 502 `{"error":"questionnaire_version_not_available"}`: la versión del
 *   cuestionario del lead no está disponible. Reintentar no lo arregla: lo tiene
 *   que revisar Operaciones.
 *
 * Aplica a GET /api/question/{product,medical}/{lead}, a los confirm
 * (/api/question/confirm…, /force-confirm) y a /api/question/validate-medical.
 * Antes la vista hacía navigate("/") y la home volvía a mandar al cuestionario,
 * que volvía a pedir el árbol: bucle de redirecciones.
 *
 * El slug nunca se enseña al cliente: la vista pinta su propio texto.
 */
export type QuestionTreeErrorKindT = "UNAVAILABLE" | "VERSION_NOT_AVAILABLE";

export const QUESTION_TREE_UNAVAILABLE = "question_tree_unavailable";
export const QUESTIONNAIRE_VERSION_NOT_AVAILABLE = "questionnaire_version_not_available";

const slugsOf = (error: unknown): string[] => {
  const { error: code, message } = (error ?? {}) as { error?: unknown; message?: unknown };
  return [code, message].filter((v): v is string => "string" === typeof v);
};

/**
 * Clasifica el error (ErrorResponse de HTTPService). Devuelve null para cualquier
 * otro error, que sigue con el tratamiento de siempre.
 *
 * Un 503 siempre es temporal, venga o no con el slug (p. ej. la página de error
 * del balanceador). Un 502 solo cuenta como «versión no disponible» con su slug:
 * un 502 genérico de pasarela no se puede presentar como «lo revisa el equipo».
 */
export const questionTreeErrorKind = (error: unknown): QuestionTreeErrorKindT | null => {
  const status = Number((error as { status?: unknown } | null)?.status);
  if (503 === status) return "UNAVAILABLE";
  if (502 === status && slugsOf(error).includes(QUESTIONNAIRE_VERSION_NOT_AVAILABLE)) {
    return "VERSION_NOT_AVAILABLE";
  }
  return null;
};

type TranslateFn = (key: string) => string;

/** `translate()` devuelve la propia clave cuando falta la traducción. */
const translateOr = (translate: TranslateFn, key: string, fallback: string): string => {
  const value = translate(key);
  return value === key || !value ? fallback : value;
};

export interface QuestionTreeErrorTextsI {
  title: string;
  body: string;
}

// Claves pendientes en getlife/translations (docs/i18n-pending.md). Mientras no
// existan, se usan estos textos.
export const QUESTION_TREE_ERROR_FALLBACKS: Record<QuestionTreeErrorKindT, QuestionTreeErrorTextsI> = {
  UNAVAILABLE: {
    title: "El cuestionario no está disponible en este momento",
    body: "Es un problema temporal de nuestro sistema. Vuelve a intentarlo en unos minutos.",
  },
  VERSION_NOT_AVAILABLE: {
    title: "Tu solicitud necesita una revisión",
    body: "No podemos mostrarte el cuestionario con los datos actuales de tu solicitud. Nuestro equipo tiene que revisarla antes de que puedas continuar.",
  },
};

export const QUESTION_TREE_RETRY_FALLBACK = "Reintentar";

const KEY_PREFIX: Record<QuestionTreeErrorKindT, string> = {
  UNAVAILABLE: "questions.treeError.unavailable",
  VERSION_NOT_AVAILABLE: "questions.treeError.versionNotAvailable",
};

export const questionTreeErrorTexts = (
  translate: TranslateFn,
  kind: QuestionTreeErrorKindT
): QuestionTreeErrorTextsI => {
  const fallback = QUESTION_TREE_ERROR_FALLBACKS[kind];
  return {
    title: translateOr(translate, `${KEY_PREFIX[kind]}.title`, fallback.title),
    body: translateOr(translate, `${KEY_PREFIX[kind]}.body`, fallback.body),
  };
};

export const questionTreeRetryText = (translate: TranslateFn): string =>
  translateOr(translate, "questions.treeError.retry", QUESTION_TREE_RETRY_FALLBACK);
